// app/javascript/projects/components/CodeDisplay.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import ColorConstants from 'constants/ColorConstants'
import DimensionsConstants from 'constants/DimensionsConstants'
import { useCodeSidebarCodes } from 'hooks/codes/useCodeSidebarCodes'
import useFilteredCodes from 'hooks/codes/useFilteredCodes'
import useSearchText from 'hooks/codes/useSearchText'
import useCodeDeletion from 'hooks/codes/useCodeDeletion';
import { useCollapseActions } from 'hooks/codes/useCollapseActions';
import useCodeTransitions from 'hooks/useCodeTransitions';

import CodeDragLayer from './CodeDragLayer'
import CodeNestedDisplay from './CodeNestedDisplay'
import DeleteCodeModal from './DeleteCodeModal';

const SEARCH_HEIGHT = 95;

// components
import FilterCodeForm from './FilterCodeForm'

const CodeDisplay = (props) => {
  const { 
    deleteCode, 
    deleteCodeHandler, 
    cancelDelete, 
    confirmDelete, 
    isDeleteModalOpen
  } = useCodeDeletion(props.projectID);
  
  const { collapseCodeBar, expandCodeBar } = useCollapseActions(props.projectID);

  const [scrollIndex, setScrollIndex] = useState(undefined);
  
  // Use custom hook for transition management
  const {
    activeTransition,
    clearTransition,
    setSelectionTransition,
    setSearchDisabledTransition
  } = useCodeTransitions();
  
  // Use the search text hook
  const { searchText, updateSearchText, clearSearchText, hasSearchText } = useSearchText();
  
  // Get all codes from the hook - let it build the complete list
  // When searching, ignore collapse state to show all codes
  const { 
    codes: allCodes = [], 
    topMargin = 0, 
    bottomMargin = 0, 
    isLoading,
    error 
  } = useCodeSidebarCodes(
    props.projectID,
    hasSearchText // Ignore collapse state when searching
  );

  // Use our enhanced hook to filter codes
  const {
    filteredCodes: codes,
    newCodeName
  } = useFilteredCodes(allCodes, searchText);

  // Keep refs for previous props to implement getDerivedStateFromProps logic
  const prevPropsRef = useRef();

  // Clear all search and scroll state
  const clearSearchAndScroll = useCallback(() => {
    setScrollIndex(undefined);
    clearSearchText();
    
    // Also clear any ongoing transition
    clearTransition();
  }, [clearSearchText, clearTransition]);

  // Clear just the scroll state (keep search text)
  const clearScrollHighlight = useCallback(() => {
    setScrollIndex(undefined);
  }, []);

  // Unified function to handle code selection/highlighting (used by both onClick and onEnter)
  const handleCodeSelection = useCallback((code, selected, fromSearch = false) => {
    const codeId = code?.id;
    
    // Only apply transitions when coming from search
    if (fromSearch) {
      // If codes are disabled, show yellow highlight
      if (props.disabled && codeId) {
        setSearchDisabledTransition(codeId);    
      } else if (codeId) {
        setSelectionTransition(codeId, selected);
      }
    }
    
    // Actually perform the selection/deselection
    props.onClick(codeId, selected);
    
  }, [props.onClick, props.disabled, setSelectionTransition, setSearchDisabledTransition]);
  
  // Handle clicking on a code
  const onClick = useCallback((code, selected) => {
    const wasSearching = hasSearchText;
    
    // If searching, clear search first to show full list
    if (hasSearchText) {
      clearSearchText();
      
      // Find the code's position in the full list
      const index = allCodes.findIndex(c => c.id === code.id);
      if (index >= 0) {
        setScrollIndex(index + 1);
      }
    }
    
    // Handle selection with animation only if we were searching
    handleCodeSelection(code, selected, wasSearching);
  }, [handleCodeSelection, hasSearchText, clearSearchText, allCodes]);


  // Add a new code
  const addNewCode = useCallback((code) => {
    props.addNewCode(code?.name);
    clearSearchAndScroll();
  }, [props.addNewCode, clearSearchAndScroll]);

  // Handle pressing Enter in search
  const onEnter = useCallback(() => {
    // If we have a new code name and no match, create it directly
    if (newCodeName) {
      addNewCode({ name: newCodeName });
      clearSearchAndScroll();
      return;
    }

    const firstCode = codes.find(code => !code.filtered);
    if (firstCode) {
      if (firstCode.newCode) {
        // Creating a new code
        setScrollIndex(0);
        addNewCode(firstCode);
        clearSearchAndScroll();
      } else {
        // Found an existing code
        const isSelected = props.selectedCodes?.includes(firstCode.id);
        
        // Clear search immediately to show full list
        clearSearchText();
        
        // Find the code's position in the full list
        const index = allCodes.findIndex(c => c.id === firstCode.id);
        if (index >= 0) {
          setScrollIndex(index + 1);
        }
        
        // Use the same function as onClick (handles both disabled and enabled states)
        // Pass true for fromSearch since onEnter is always from search context
        handleCodeSelection(firstCode, isSelected, true);
      }
    }
  }, [codes, props.selectedCodes, allCodes, props.disabled, handleCodeSelection, clearSearchAndScroll, newCodeName, addNewCode, clearSearchText]);

  // Handle search text changes
  const filterText = useCallback((text) => {
    updateSearchText(text);
    clearScrollHighlight();
    
    // Clear any transition when searching to prevent interrupted/repeated animations
    clearTransition();
  }, [updateSearchText, clearScrollHighlight, clearTransition]);

  // Other action handlers that should clear search/scroll state
  const initiateMergeCode = useCallback(() => {
    clearScrollHighlight();
  }, [clearScrollHighlight]);

  const onCollapseClicked = useCallback((codeId, collapsed) => {
    clearScrollHighlight();

    if (collapsed) {
      expandCodeBar(codeId);
    } else {
      collapseCodeBar(codeId);
    }
  }, [clearScrollHighlight, expandCodeBar, collapseCodeBar]);

  const startDrag = useCallback(() => {
    clearScrollHighlight();
  }, [clearScrollHighlight]);


  // Effect to replace getDerivedStateFromProps logic
  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (prevProps) {
      const propsSelect = props.select || {};
      const prevPropsSelect = prevProps.select || {};

      if (props.disabled !== prevProps.disabled ||
        propsSelect.start !== prevPropsSelect.start ||
        propsSelect.end !== prevPropsSelect.end ||
        propsSelect.codeableId !== prevPropsSelect.codeableId)
      {
        clearSearchAndScroll();
      }

      if (props.projectID != prevProps.projectID) {
        clearSearchAndScroll();
      }
    }

    prevPropsRef.current = props;
  }, [props, searchText, clearSearchAndScroll]);

  // Clear search state when searchText changes
  useEffect(() => {
    if (searchText.length !== 0) {
      clearScrollHighlight();
    }
  }, [searchText, clearScrollHighlight]);

  // Height now handled by flexbox

  return (
    <div style={{ paddingLeft: "0px", height: "100%", display: "flex", flexDirection: "column" }} className='code-display'>
      <CodeDragLayer projectId={props.projectID} />
      <DeleteCodeModal
        show={isDeleteModalOpen}
        name={deleteCode.name}
        onCancel={cancelDelete}
        onDelete={confirmDelete}
      />

      <style type="text/css">{`
        .loading-indicator {
          padding: 15px;
          text-align: center;
          color: #666;
        }

        .error-message {
          padding: 15px;
          text-align: center;
          color: #d9534f;
          background-color: #f9f2f2;
          border-radius: 4px;
          margin: 10px;
        }
      `}</style>

      <div style={{ paddingLeft: DimensionsConstants.CODE_PADDING_LEFT, flexShrink: 0, paddingTop: "10px" }}>
        <h2 style={{ cursor: "default", marginTop: 0 }}>Codes</h2>
        <FilterCodeForm
          filterText={filterText}
          searchText={searchText}
          onEnter={onEnter}
          select={props.select}
        />
      </div>

      <div style={{ flex: 1, overflow: "hidden" }}>
        {error && (
          <div className="error-message">
            Error loading codes: {error.message}
          </div>
        )}
        <CodeNestedDisplay
          initiateMergeCode={initiateMergeCode}
          selectedCodes={props.selectedCodes}
          onClick={onClick}
          disabled={props.disabled}
          deleteCode={deleteCodeHandler}
          addNewCode={addNewCode}
          projectID={props.projectID}
          codes={codes} // This is now the filtered codes list
          topMargin={topMargin}
          bottomMargin={bottomMargin}
          draggingEnabled={!hasSearchText}
          onCollapseClicked={onCollapseClicked}
          searchText={searchText}
          isSearching={hasSearchText}
          startDrag={startDrag}
          scrollIndex={scrollIndex}
          clearScrollIndex={clearScrollHighlight}
          hideCodeCounts={props.hideCodeCounts}
          showEmptyStateOnboarding={props.showEmptyStateOnboarding}
          newCodeName={newCodeName}
          activeTransition={activeTransition}
        />
      </div>
    </div>
  );
};

export default CodeDisplay;